import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import { PROJECT_TYPES } from '@jobos/shared';
import type { BreakdownDocumentContents } from '@jobos/shared';
import { useCreateTemplateFromJob } from '@/hooks/useBreakdownTemplates';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobProjectType: string;
  /**
   * When supplied, the live editor contents are sent instead of letting the
   * server snapshot the job's Active document. Only the breakdown editor can
   * provide these; the Overview card has no editor state to serialize.
   */
  getContents?: () => BreakdownDocumentContents;
}

const PROJECT_TYPE_OPTIONS = PROJECT_TYPES.map((t) => ({ value: t, label: t }));

export function SaveAsTemplateDialog({
  open,
  onOpenChange,
  jobId,
  jobProjectType,
  getContents,
}: Props) {
  const [name, setName] = useState('');
  const [projectType, setProjectType] = useState(jobProjectType);
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateTemplateFromJob();

  useEffect(() => {
    if (open) {
      setName('');
      setProjectType(jobProjectType);
      setIsDefault(false);
      setError(null);
    }
  }, [open, jobProjectType]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    if (!projectType) {
      setError('Project type is required');
      return;
    }
    try {
      await create.mutateAsync({
        jobId,
        projectType,
        name: trimmed,
        isDefault,
        contents: getContents?.(),
      });
      onOpenChange(false);
    } catch (e: any) {
      if (e.message?.toLowerCase().includes('already exists')) {
        setError(e.message);
      } else {
        setError(e.message ?? 'Failed to save template');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save as template</DialogTitle>
          <DialogDescription>
            Save this job's current breakdown as a reusable job type template.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Project type</label>
            <Combobox
              value={projectType}
              onValueChange={(value) => {
                setProjectType(value as string);
                if (error) setError(null);
              }}
              options={PROJECT_TYPE_OPTIONS}
              placeholder="Select a project type"
              searchPlaceholder="Search..."
              emptyText="No project type found."
            />
          </div>
          <div>
            <label htmlFor="template-name" className="text-sm font-medium text-gray-700">
              Template name
            </label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              placeholder="e.g., Standard 1500sqft roof"
              className="mt-2"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                }
              }}
            />
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Set as default for this project type
          </label>
          <p className="text-xs text-gray-500">
            Templates capture line items, projects, and sub-items.
            {getContents
              ? ' Tax/markup rates and rounding are saved with the template.'
              : ' Tax/markup rates are not included.'}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!name.trim() || !projectType || create.isPending}>
            {create.isPending ? 'Saving...' : 'Save template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
